import type CategoryActionSource from 'owa-categories/lib/utils/CategoryActionSource';
import { getCategoryMenuProps } from 'owa-categories/lib/utils/getCategoryMenuProps';
import getMasterCategoryList from 'owa-categories/lib/utils/getMasterCategoryList';
import type { IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import { DirectionalHint } from '@fluentui/react/lib/ContextualMenu';
import { isMonarchMultipleAccountsEnabled } from 'owa-account-source-list';
import { lazyAddFavoriteCategory } from 'owa-favorites';
import type { TableView } from 'owa-mail-list-store';
import { getStore as getListViewStore } from 'owa-mail-list-store';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import type { MailboxInfo } from 'owa-client-types';
import type { ActionSource } from 'owa-mail-store';
import {
    lazyAddCategoriesFromTable,
    lazyClearCategoriesFromTable,
    lazyRemoveCategoriesFromTable,
    lazyAddCategoriesFromItem,
    lazyClearCategoriesFromItem,
    lazyRemoveCategoriesFromItem,
} from 'owa-mail-triage-action';
import selectMailStoreItemById from 'owa-mail-store/lib/selectors/selectMailStoreItemById';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { lazyMountAndShowFullOptions } from 'owa-options-view';
import { CATEGORY_MENU_WIDTH } from './constants';
import {
    getAllCategoriesOnRows,
    getCommonCategoriesOnRows,
} from './getCategoryActionDecisionProperties';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isSharedMailboxInfo } from 'owa-client-types/lib/SharedMailboxInfo';
import { getMailCategorizeCallbacks } from 'owa-calendar-unified-telemetry-utils/lib/utils/logMailCategorizeTelemetry';
import {
    type UTCalendarEventFeatureAction,
    type UTCalendarUIContext,
    type UTCalendarOrigin,
} from 'owa-unified-telemetry';
import type CategoryType from 'owa-service/lib/contract/CategoryType';

/**
 * Helper function to log mail category telemetry for context menu actions
 * This wraps the common pattern of getting callbacks and logging success
 */
function logCategorizeUnifiedTelemetry(
    action: UTCalendarEventFeatureAction,
    uiContext: UTCalendarUIContext,
    origin: UTCalendarOrigin
): void {
    const { onSuccess } = getMailCategorizeCallbacks(action, uiContext, origin);
    onSuccess();
}

/**
 * Gets the category menu items for context menu
 * @param tableViewId the tableView id
 * @param dismissMenu the callback to be called to dismiss of menu
 * @param actionSource the actionSource
 * @param shouldShowSearch flag indicating whether to show search box or not
 * @param itemId the selected item
 * @param isItemPart flag indicating whether the context menu is for single item in conversation view
 * @return returns the contextual menu items
 */
export function getCategoryMenuPropertiesForContextMenu(
    tableViewId: string,
    dismissMenu: (ev?: any) => void,
    actionSource: ActionSource,
    shouldShowSearch?: boolean,
    itemId?: string,
    isItemPart?: boolean
): IContextualMenuProps {
    const tableView = getListViewStore().tableViews.get(tableViewId);
    return isItemPart
        ? getCategoriesMenuItemProperties(
              getListViewStore().tableViews.get(tableViewId),
              'ContextLV',
              itemId,
              dismissMenu,
              DirectionalHint.rightTopEdge
          )
        : getCategoriesMenuProperties(
              tableView,
              shouldShowSearch ?? false /* showSearchBox */,
              dismissMenu, // Callback to dismiss the right click context menu
              actionSource,
              'ContextLV',
              DirectionalHint.rightTopEdge
          );
}

/**
 * Gets the category menu items
 * @param tableView the tableView
 * @param showSearchBox flag indicating whether to show search box or not
 * @param dismissMenu callback to be called to dismiss the category context menu
 * @param actionSource the actionSource
 * @param categoryActionSource the category action source
 * @param directionalHint determines which direction the callout appears, defaults to bottomLeftEdge
 * @return returns the contextual menu items
 */
function getCategoriesMenuProperties(
    tableView: TableView | undefined,
    showSearchBox: boolean,
    dismissMenu: () => void,
    actionSource: ActionSource,
    categoryActionSource: CategoryActionSource,
    directionalHint?: DirectionalHint
): IContextualMenuProps {
    if (!tableView) {
        return { items: [] };
    }
    const mailboxInfo = getMailboxInfo(tableView);
    const categoryMenuProps = getCategoryMenuProps({
        actionSource: categoryActionSource,
        categoryList: getMasterCategoryList(mailboxInfo),
        mailboxInfo,
        getCategoriesToCheck: () =>
            getCommonCategoriesOnRows(tableView, [...tableView.selectedRowKeys.keys()]),
        dismissMenu,
        shouldShowSearchBox: showSearchBox,
        shouldShowManageCategories: !isSharedMailboxInfo(mailboxInfo),
        getShouldShowClearCategories: () => {
            return (
                getAllCategoriesOnRows(tableView, [...tableView.selectedRowKeys.keys()]).length > 0
            );
        }, // Show clear categories if any of the selected row has categories
        addFavoriteCategory,
        onAfterCreateNewCategory: (
            category: string,
            createNewCategoryPromise?: Promise<boolean>
        ) => {
            // Log telemetry for new category action
            logCategorizeUnifiedTelemetry('New', 'MailList', 'SurfaceContextMenu');

            // When a new category is created, we should apply it optimistically to the selected items
            lazyAddCategoriesFromTable.importAndExecute(
                [...tableView.selectedRowKeys.keys()],
                tableView,
                [category],
                actionSource,
                createNewCategoryPromise
            );
        },
        onCheckedCategoryClicked: category => {
            // Log telemetry for unselect category action
            logCategorizeUnifiedTelemetry('UnselectItem', 'MailList', 'SurfaceContextMenu');

            // When a checked category is clicked its a remove action
            lazyRemoveCategoriesFromTable.importAndExecute(
                [...tableView.selectedRowKeys.keys()],
                tableView,
                [category],
                actionSource
            );
        },
        onUncheckedCategoryClicked: category => {
            // Log telemetry for select category action
            logCategorizeUnifiedTelemetry('SelectItem', 'MailList', 'SurfaceContextMenu');

            // When an unchecked category is clicked its an add action
            lazyAddCategoriesFromTable.importAndExecute(
                [...tableView.selectedRowKeys.keys()],
                tableView,
                [category],
                actionSource
            );
        },
        onManageCategoryClicked: () => {
            // Log telemetry for manage categories action
            logCategorizeUnifiedTelemetry('Manage', 'MailList', 'SurfaceContextMenu');

            onManageCategoryClicked(mailboxInfo);
        },
        onClearCategoriesClicked: () => {
            // Log telemetry for clear categories action
            logCategorizeUnifiedTelemetry('Clear', 'MailList', 'SurfaceContextMenu');

            lazyClearCategoriesFromTable.importAndExecute(
                [...tableView.selectedRowKeys.keys()],
                tableView,
                actionSource
            );
        },
        directionalHint,
        width: CATEGORY_MENU_WIDTH,
    });

    return categoryMenuProps;
}

/**
 * Gets the category menu items
 * @param tableView the tableView
 * @param categoryActionSource the category action source
 * @param itemId the selected item
 * @param dismissMenu the callback to be called to dismiss of menu
 * @param directionalHint determines which direction the callout appears, defaults to bottomLeftEdge
 * @return returns the contextual menu items
 */
function getCategoriesMenuItemProperties(
    tableView: TableView | undefined,
    categoryActionSource: CategoryActionSource,
    itemId: string | undefined,
    dismissMenu: () => void,
    directionalHint: DirectionalHint
): IContextualMenuProps {
    if (!tableView || !itemId) {
        return { items: [] };
    }
    const item = selectMailStoreItemById(itemId);
    const mailboxInfo = item.MailboxInfo ?? getMailboxInfo(tableView);
    const categoryMenuProps = getCategoryMenuProps({
        actionSource: categoryActionSource,
        categoryList: getMasterCategoryList(mailboxInfo),
        mailboxInfo,
        getCategoriesToCheck: () => {
            return item.Categories ?? [];
        },
        dismissMenu,
        shouldShowSearchBox: true,
        shouldShowManageCategories: !isSharedMailboxInfo(mailboxInfo),
        getShouldShowClearCategories: () => {
            return false;
        },
        addFavoriteCategory,
        onAfterCreateNewCategory: (category: string) => {
            // Get telemetry callbacks for new category action (single item)
            const { onSuccess } = getMailCategorizeCallbacks(
                'New',
                'MailReadingPane',
                'SurfaceItemContextMenu'
            );

            // When a new category is created, we should apply it optimistically to the selected items
            lazyAddCategoriesFromItem.importAndExecute(
                itemId,
                mailboxInfo,
                [category],
                undefined /* createNewCategoryPromise */,
                categoryActionSource
            );

            // Log successful telemetry
            onSuccess();
        },
        onCheckedCategoryClicked: (category: string) => {
            // Get telemetry callbacks for unselect category action (single item)
            const { onSuccess } = getMailCategorizeCallbacks(
                'UnselectItem',
                'MailReadingPane',
                'SurfaceItemContextMenu'
            );

            // When a checked category is clicked its a remove action
            lazyRemoveCategoriesFromItem.importAndExecute(
                itemId,
                mailboxInfo,
                [category],
                categoryActionSource
            );

            // Log successful telemetry
            onSuccess();
        },
        onUncheckedCategoryClicked: (category: string) => {
            // Get telemetry callbacks for select category action (single item)
            const { onSuccess } = getMailCategorizeCallbacks(
                'SelectItem',
                'MailReadingPane',
                'SurfaceItemContextMenu'
            );

            // When an unchecked category is clicked its an add action
            lazyAddCategoriesFromItem.importAndExecute(
                itemId,
                mailboxInfo,
                [category],
                undefined /* createNewCategoryPromise */,
                categoryActionSource
            );

            // Log successful telemetry
            onSuccess();
        },
        onManageCategoryClicked: () => {
            // Get telemetry callbacks for manage categories action (single item)
            const { onSuccess } = getMailCategorizeCallbacks(
                'Manage',
                'MailReadingPane',
                'SurfaceItemContextMenu'
            );

            onManageCategoryClicked(mailboxInfo);

            // Log successful telemetry
            onSuccess();
        },
        onClearCategoriesClicked: () => {
            // Get telemetry callbacks for clear categories action (single item)
            const { onSuccess } = getMailCategorizeCallbacks(
                'Clear',
                'MailReadingPane',
                'SurfaceItemContextMenu'
            );

            lazyClearCategoriesFromItem.importAndExecute(itemId, mailboxInfo);

            // Log successful telemetry
            onSuccess();
        },
        directionalHint,
        width: CATEGORY_MENU_WIDTH,
    });

    return categoryMenuProps;
}

/**
 * Custom callback function when manage category is clicked
 */
function onManageCategoryClicked(mailboxInfo: MailboxInfo) {
    // Navigate to options/categories
    if (isMonarchMultipleAccountsEnabled() || isFeatureEnabled('acct-multiacctcomingsoon')) {
        lazyMountAndShowFullOptions.importAndExecute(
            'accounts-category',
            'categories',
            undefined,
            undefined,
            mailboxInfo
        );
    } else {
        lazyMountAndShowFullOptions.importAndExecute('accounts-category', 'categories');
    }
}

/**
 * Custom callback function when adding a favorite category
 */
function addFavoriteCategory(categoryId: string, actionSource: CategoryActionSource): void {
    lazyAddFavoriteCategory
        .import()
        .then(addFavoriteCategoryFunc =>
            addFavoriteCategoryFunc(categoryId, actionSource, getModuleContextMailboxInfo())
        );
}

export function getCategoryMenuContextCallbacks(
    selectedRowKeys: string[],
    tableView: TableView,
    mailboxInfo: MailboxInfo,
    actionSource: ActionSource,
    isGroupHeaderSelected: boolean, // Used for group header actions, checked categories are not shown and the clear categories button always shown
    getTargetRowKeysFunction?: () => Promise<string[]>
): {
    getCategoriesToCheck: () => string[];
    getShouldShowClearCategories: () => boolean;
    onAfterCreateNewCategory: (
        category: string,
        createNewCategoryPromise?: Promise<boolean>
    ) => Promise<void>;
    addFavoriteCategory: typeof addFavoriteCategory;
    onCheckedCategoryClicked: (category: string) => Promise<void>;
    onUncheckedCategoryClicked: (category: string) => Promise<void>;
    categoryList: CategoryType[];
    onManageCategoryClicked: () => void;
    onClearCategoriesClicked: () => Promise<void>;
} {
    const callbacks = {
        getCategoriesToCheck: () =>
            isGroupHeaderSelected ? [] : getCommonCategoriesOnRows(tableView, selectedRowKeys),
        getShouldShowClearCategories: () =>
            isGroupHeaderSelected || getAllCategoriesOnRows(tableView, selectedRowKeys).length > 0,
        onAfterCreateNewCategory: async (
            category: string,
            createNewCategoryPromise?: Promise<boolean>
        ) => {
            const targetRowKeys = getTargetRowKeysFunction
                ? await getTargetRowKeysFunction()
                : selectedRowKeys;

            // When a new category is created, we should apply it optimistically to the selected items
            lazyAddCategoriesFromTable.importAndExecute(
                targetRowKeys,
                tableView,
                [category],
                actionSource,
                createNewCategoryPromise
            );
        },
        addFavoriteCategory,
        onCheckedCategoryClicked: async (category: string) => {
            const targetRowKeys = getTargetRowKeysFunction
                ? await getTargetRowKeysFunction()
                : selectedRowKeys;

            lazyRemoveCategoriesFromTable.importAndExecute(
                targetRowKeys,
                tableView,
                [category],
                actionSource
            );
        },
        onUncheckedCategoryClicked: async (category: string) => {
            const targetRowKeys = getTargetRowKeysFunction
                ? await getTargetRowKeysFunction()
                : selectedRowKeys;

            // When an unchecked category is clicked its an add action
            lazyAddCategoriesFromTable.importAndExecute(
                targetRowKeys,
                tableView,
                [category],
                actionSource
            );
        },
        categoryList: getMasterCategoryList(mailboxInfo),
        onManageCategoryClicked: () => onManageCategoryClicked(mailboxInfo),
        onClearCategoriesClicked: async () => {
            const targetRowKeys = getTargetRowKeysFunction
                ? await getTargetRowKeysFunction()
                : selectedRowKeys;

            lazyClearCategoriesFromTable.importAndExecute(targetRowKeys, tableView, actionSource);
        },
    };
    return callbacks;
}

export function getCategoryPartItemMenuContextCallbacks(
    mailboxInfo: MailboxInfo,
    categoryActionSource: CategoryActionSource,
    itemId: string
): {
    onAfterCreateNewCategory: (category: string) => void;
    addFavoriteCategory: typeof addFavoriteCategory;
    onCheckedCategoryClicked: (category: string) => void;
    onUncheckedCategoryClicked: (category: string) => void;
    categoryList: CategoryType[];
    getCategoriesToCheck: () => string[];
    getShouldShowClearCategories: () => boolean;
    onManageCategoryClicked: () => void;
    onClearCategoriesClicked: () => void;
} {
    const item = selectMailStoreItemById(itemId);
    mailboxInfo = item.MailboxInfo ?? mailboxInfo;
    const callbacks = {
        onAfterCreateNewCategory: (category: string) => {
            // Get telemetry callbacks for new category action (part item context)
            const { onSuccess } = getMailCategorizeCallbacks(
                'New',
                'MailReadingPane',
                'SurfaceItemContextMenu'
            );

            // When a new category is created, we should apply it optimistically to the selected items
            lazyAddCategoriesFromItem.importAndExecute(
                itemId,
                mailboxInfo,
                [category],
                undefined /* createNewCategoryPromise */,
                categoryActionSource
            );

            // Log successful telemetry
            onSuccess();
        },
        addFavoriteCategory,
        onCheckedCategoryClicked: (category: string) => {
            // Get telemetry callbacks for unselect category action (part item context)
            const { onSuccess } = getMailCategorizeCallbacks(
                'UnselectItem',
                'MailReadingPane',
                'SurfaceItemContextMenu'
            );

            // When a checked category is clicked its a remove action
            lazyRemoveCategoriesFromItem.importAndExecute(
                itemId,
                mailboxInfo,
                [category],
                categoryActionSource
            );

            // Log successful telemetry
            onSuccess();
        },
        onUncheckedCategoryClicked: (category: string) => {
            // Get telemetry callbacks for select category action (part item context)
            const { onSuccess } = getMailCategorizeCallbacks(
                'SelectItem',
                'MailReadingPane',
                'SurfaceItemContextMenu'
            );

            // When an unchecked category is clicked its an add action
            lazyAddCategoriesFromItem.importAndExecute(
                itemId,
                mailboxInfo,
                [category],
                undefined /* createNewCategoryPromise */,
                categoryActionSource
            );

            // Log successful telemetry
            onSuccess();
        },
        categoryList: getMasterCategoryList(mailboxInfo),
        getCategoriesToCheck: () => {
            return item.Categories ?? [];
        },
        getShouldShowClearCategories: () => {
            return false;
        },
        onManageCategoryClicked: () => {
            // Get telemetry callbacks for manage categories action (part item context)
            const { onSuccess } = getMailCategorizeCallbacks(
                'Manage',
                'MailReadingPane',
                'SurfaceItemContextMenu'
            );

            onManageCategoryClicked(mailboxInfo);

            // Log successful telemetry
            onSuccess();
        },
        onClearCategoriesClicked: () => {
            // Get telemetry callbacks for clear categories action (part item context)
            const { onSuccess } = getMailCategorizeCallbacks(
                'Clear',
                'MailReadingPane',
                'SurfaceItemContextMenu'
            );

            lazyClearCategoriesFromItem.importAndExecute(itemId, mailboxInfo);

            // Log successful telemetry
            onSuccess();
        },
    };
    return callbacks;
}
